<template>
    <fieldset
        v-if="control.visible"
        :class="styles.arrayList.root"
    >
        <legend :class="styles.arrayList.legend">
            <DButton
                :class="styles.arrayList.addButton"
                :disabled="
                    !control.enabled || (appliedOptions.restrict && maxItemsReached)
                "
                icon="plus"
                color="primary"
                @click.prevent="addButtonClick"
            />
            <label :class="styles.arrayList.label">
                {{ control.label }}
            </label>
        </legend>
        <div
            v-for="(_, index) in control.data"
            :key="`${control.path}-${index}`"
            :class="styles.arrayList.itemWrapper"
        >
            <ArrayListElement
                :move-up="moveUp(control.path, index)"
                :move-up-enabled="control.enabled && index > 0"
                :move-down="moveDown(control.path, index)"
                :move-down-enabled="control.enabled && index < control.data.length - 1"
                :delete-enabled="control.enabled && !minItemsReached"
                :delete="removeItems(control.path, [index])"
                :label="childLabelForIndex(index)"
                :styles="styles"
            >
                <dispatch-renderer
                    :schema="control.schema"
                    :uischema="childUiSchema"
                    :path="composePaths(control.path, `${index}`)"
                    :enabled="control.enabled"
                    :renderers="control.renderers"
                    :cells="control.cells"
                />
            </ArrayListElement>
        </div>
        <div
            v-if="noData"
            :class="styles.arrayList.noData"
        >
            {{ translations.noDataMessage }}
        </div>
    </fieldset>
</template>

<script>
import {
    arrayDefaultTranslations,
    composePaths,
    createDefaultValue,
    defaultJsonFormsI18nState,
    getArrayTranslations,
    rankWith,
    Resolve,
    schemaTypeIs,
} from '@jsonforms/core'
import { defineComponent, inject } from 'vue'
import ArrayListElement from './ArrayListElement.vue'
import { DispatchRenderer, rendererProps, useJsonFormsArrayControl, } from '@jsonforms/vue'
import { useVanillaArrayControl } from '@jsonforms/vue-vanilla'

const controlRenderer = defineComponent({
    name: 'ArrayListRenderer',
    components: {
        ArrayListElement,
        DispatchRenderer,
    },
    props: {
        ...rendererProps(),
    },
    setup (props) {
        const control = useVanillaArrayControl(useJsonFormsArrayControl(props))

        return {
            ...control,
            childLabelForIndex: (index) => {
                const getLabelForItem = props.uischema?.options?.getLabelForItem
                if (typeof getLabelForItem === 'function') {
                    return getLabelForItem(control.control.value.data[index], index)
                }

                const fallbackKey = props.uischema?.options?.childLabelProp ?? 'name'

                return control.control.value.data[index][fallbackKey] ?? `Item ${index + 1}`
            }
        }
    },
    computed: {
        noData () {
            return !this.control.data || this.control.data.length === 0
        },
        arraySchema () {
            return Resolve.schema(
                this.schema,
                this.control.uischema.scope,
                this.control.rootSchema
            )
        },
        maxItemsReached () {
            return (
                this.arraySchema !== undefined &&
                this.arraySchema.maxItems !== undefined &&
                this.control.data !== undefined &&
                this.control.data.length >= this.arraySchema.maxItems
            )
        },
        minItemsReached () {
            return (
                this.arraySchema !== undefined &&
                this.arraySchema.minItems !== undefined &&
                this.control.data !== undefined &&
                this.control.data.length <= this.arraySchema.minItems
            )
        },
        translations () {
            const jsonforms = inject('jsonforms')
            return getArrayTranslations(
                jsonforms?.i18n?.translate ?? defaultJsonFormsI18nState.translate,
                arrayDefaultTranslations,
                this.control.i18nKeyPrefix,
                this.control.label
            )
        },
    },
    methods: {
        composePaths,
        createDefaultValue,
        addButtonClick () {
            this.addItem(
                this.control.path,
                createDefaultValue(this.control.schema, this.control.rootSchema)
            )()
        },
    },
})

export default controlRenderer

export const entry = {
    renderer: controlRenderer,
    tester: rankWith(2, schemaTypeIs('array')),
}
</script>
